import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Space,
  Typography,
  Collapse,
  Spin,
  Empty,
  Tooltip,
  message,
  ConfigProvider,
  Grid,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  SettingOutlined,
  BlockOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import BlockCard from "./builder/BlockCard";
import ComponentLibrary from "./builder/ComponentLibrary";
import AddBetweenButton from "./builder/AddBetweenButton";
import {
  convertLegacyDataToSections,
  getGroupDefaultSection,
  getSectionDefinition,
} from "./builder/autoload";
import {
  PAGE_GROUPS,
  CMS_LANGUAGES,
  getSlugParts,
  stripSlugPrefix,
  SERVICE_GROUP,
} from "./builder/constant";
import DynamicVariables from "./builder/DynamicVariables";
import { refreshCmsPageOptions } from "../../components/cms-common/LinkPicker";
import { exceptionSlugs } from "../../constants/exceptionSlug";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const createSection = (secDef) => ({
  id: `sec-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
  type: secDef.type,
  title: secDef.name,
  enabled: true,
  data: JSON.parse(JSON.stringify(secDef.defaultData)),
});

// Modals (icon/media pickers) render on document.body, so they must sit above
// the fixed full-screen editor overlay (zIndex 999999).
const EDITOR_THEME = { token: { zIndexPopupBase: 1000000 } };

// Helper function to slugify title
const slugify = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-");

const EditCMSPage = ({
  formFor,
  title: initialTitle,
  slug: initialSlug,
  mode,
  editLayout,
  onBack,
  singlePageLoading,
  activeTab,
}) => {
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const isDesktop = screens.lg !== false;
  const [sections, setSections] = useState([]);
  const [pageTitle, setPageTitle] = useState(
    initialTitle || formFor?.title || "",
  );

  const queryParams = new URLSearchParams(window.location.search);
  const urlGroup = queryParams.get("group");
  const [pageSlug, setPageSlug] = useState(
    stripSlugPrefix(
      initialSlug || formFor?.slug || "",
      formFor?.group || urlGroup,
    ),
  );
  const defaultGroup =
    formFor?.group ||
    urlGroup ||
    (activeTab && activeTab !== "all" ? activeTab : "page");

  const [pageGroup, setPageGroup] = useState(defaultGroup);
  const [pageStatus, setPageStatus] = useState(formFor?.status || "ACTIVE");
  const [currentLang, setCurrentLang] = useState("en");
  const [isAutoSlug, setIsAutoSlug] = useState(
    mode === "add" || !formFor?.slug,
  );
  const [seoSetting, setSeoSetting] = useState({});
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [insertAt, setInsertAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const langData = formFor?.[currentLang]?.data || formFor?.en?.data;
    if (langData) {
      const parsedSections = convertLegacyDataToSections(
        langData,
        initialSlug || formFor?.slug,
        formFor?.group || urlGroup,
      );
      setSections(parsedSections);
    } else {
      setSections([]);
    }
    if (formFor?.seo_setting) {
      const seoObj = Array.isArray(formFor.seo_setting)
        ? formFor.seo_setting[0]
        : formFor.seo_setting;
      setSeoSetting(seoObj || {});
    }
    if (initialTitle || formFor?.title) {
      setPageTitle(initialTitle || formFor?.title);
    }
    if (initialSlug || formFor?.slug) {
      setPageSlug(
        stripSlugPrefix(
          initialSlug || formFor?.slug,
          formFor?.group || urlGroup,
        ),
      );
    }
    if (formFor?.group) {
      setPageGroup(formFor.group);
    } else if (urlGroup) {
      setPageGroup(urlGroup);
    }
    if (formFor?.status) {
      setPageStatus(formFor.status);
    }
    if (mode !== "add") {
      setIsAutoSlug(false);
    }
  }, [formFor, initialSlug, initialTitle, currentLang, urlGroup, mode]);

  // Link pickers should list pages created since the last editor session.
  useEffect(() => {
    refreshCmsPageOptions();
  }, []);

  // New header/footer pages start with their layout section pre-filled.
  useEffect(() => {
    if (mode !== "add") return;
    const def = getGroupDefaultSection(pageGroup);
    if (!def) return;
    setSections((prev) => (prev.length > 0 ? prev : [createSection(def)]));
    setExpandedIndex(0);
  }, [mode, pageGroup]);

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setPageTitle(val);
    if (isAutoSlug) {
      setPageSlug(slugify(val));
    }
  };

  const handleSlugChange = (e) => {
    setIsAutoSlug(false);
    setPageSlug(e.target.value);
  };

  const { prefix, suffix } = getSlugParts(pageGroup);
  const isServicePage = pageGroup === SERVICE_GROUP;

  // Appends a variable to an SEO field.
  const appendToSeo = (field) => (token) =>
    setSeoSetting((prev) => {
      const current = prev?.[field] || "";
      return { ...prev, [field]: current ? `${current} ${token}` : token };
    });

  const handleGroupChange = (val) => {
    setPageGroup(val);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("group", val);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${searchParams.toString()}`,
    );
    if (isAutoSlug && pageTitle) {
      setPageSlug(slugify(pageTitle));
    }
  };

  const handleUpdateSection = (index, updatedSection) => {
    const updated = [...sections];
    updated[index] = updatedSection;
    setSections(updated);
  };

  const handleMoveSection = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sections.length) return;
    const updated = [...sections];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setSections(updated);
    setExpandedIndex(toIndex);
  };

  const handleDuplicateSection = (index) => {
    const target = sections[index];
    if (getSectionDefinition(target?.type)?.singleton) {
      message.warning(`"${target.title}" can only be added once per page.`);
      return;
    }
    const duplicated = {
      ...JSON.parse(JSON.stringify(target)),
      id: `sec-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      title: `${target.title || target.type} (Copy)`,
    };
    const updated = [...sections];
    updated.splice(index + 1, 0, duplicated);
    setSections(updated);
    setExpandedIndex(index + 1);
    message.info(`Duplicated "${target.title}" block.`);
  };

  const handleDeleteSection = (index) => {
    const removedTitle = sections[index]?.title;
    const updated = sections.filter((_, idx) => idx !== index);
    setSections(updated);
    if (expandedIndex >= updated.length) {
      setExpandedIndex(Math.max(0, updated.length - 1));
    }
    message.success(`Removed "${removedTitle}" block.`);
  };

  const handleAddSection = (secDef) => {
    if (
      getSectionDefinition(secDef.type)?.singleton &&
      sections.some((sec) => sec.type === secDef.type)
    ) {
      message.warning(`"${secDef.name}" can only be added once per page.`);
      return;
    }
    const newSection = createSection(secDef);

    const updated = [...sections];
    let newIndex = updated.length;

    if (insertAt !== null && insertAt >= 0 && insertAt <= updated.length) {
      updated.splice(insertAt + 1, 0, newSection);
      newIndex = insertAt + 1;
      setInsertAt(null);
    } else {
      updated.push(newSection);
    }

    setSections(updated);
    setExpandedIndex(newIndex);
    message.success(`Added "${newSection.title}" block.`);
  };

  const handleSavePage = async () => {
    if (!pageTitle.trim()) {
      message.error("Please enter a page title.");
      return;
    }

    try {
      setSaving(true);
      const fullSlug = `${prefix || ""}${stripSlugPrefix(pageSlug, pageGroup)}${suffix || ""}`;
      let updatedData = {
        ...(formFor?.[currentLang]?.data || {}),
        sections: sections,
      };

      if (
        pageGroup === "blogs" ||
        pageGroup === "blog" ||
        pageGroup === "blog_page"
      ) {
        const blogSection = sections.find((s) => s.type === "BlogDetail");
        if (blogSection) {
          const data = blogSection.data;
          updatedData = {
            ...updatedData,
            title: data.title || pageTitle,
            slug: pageSlug,
            content: {
              heading: data.heading || "",
              description: data.content || "",
            },
            excerpt: {
              description: data.excerpt || "",
            },
            feature_image: data.feature_image
              ? Array.isArray(data.feature_image)
                ? data.feature_image
                : [{ url: data.feature_image, is_visible: true }]
              : [],
            tags: data.tags || [],
            blog_category_id: data.category || 4,
            visibility_status:
              data.visibility_status !== undefined
                ? data.visibility_status
                : true,
            visibility_date: data.visibility_date || null,
          };
          // Remove sections if it's strictly a flat blog object
          delete updatedData.sections;
        }
      }

      await editLayout(
        updatedData,
        pageTitle,
        seoSetting,
        fullSlug,
        pageGroup,
        pageStatus,
        currentLang,
      );
    } catch (err) {
      console.error(err);
      message.error("Failed to save page layout.");
    } finally {
      setSaving(false);
    }
  };

  if (singlePageLoading) {
    return (
      <Card style={{ textAlign: "center", padding: 60, minHeight: "60vh" }}>
        <Spin size="large" tip="Loading CMS Page Builder Canvas..." />
      </Card>
    );
  }

  return ReactDOM.createPortal(
    <ConfigProvider
      theme={EDITOR_THEME}
      getPopupContainer={(triggerNode) =>
        triggerNode?.closest("#cms-editor-overlay") || document.body
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSavePage}
        style={{ width: "100%", height: "100%" }}
      >
        <div
          id="cms-editor-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 999999,
            background: "#f8fafc",
            overflowY: "auto",
            padding: isDesktop ? "14px 20px 60px 20px" : "10px 10px 60px 10px",
          }}
        >
          {/* Merged Single Header Toolbar Card */}
          <Card
            style={{ marginBottom: 12, borderRadius: 8 }}
            bodyStyle={{ padding: "10px 16px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              {/* Left: Back Button & Title */}
              <Space size="small" style={{ flexShrink: 0 }}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={onBack}
                  size="middle"
                >
                  Back
                </Button>
                <Title
                  level={5}
                  style={{ margin: 0, whiteSpace: "nowrap", fontSize: 16 }}
                >
                  {mode === "add" ? "Create Page" : "Edit Page"}
                </Title>
              </Space>

              {/* Center Form Controls: Group Select, Page Name Input, Slug Input & Language Switcher */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flex: "1 1 520px",
                  minWidth: 0,
                  flexWrap: "wrap",
                  order: isDesktop ? 0 : 1,
                }}
              >
                {/* Page Group Select */}
                <div style={{ flex: "1 1 120px", maxWidth: 180 }}>
                  <Select
                    style={{ width: "100%" }}
                    value={pageGroup}
                    onChange={handleGroupChange}
                    options={PAGE_GROUPS.map((g) => ({
                      label: g.label,
                      value: g.value,
                    }))}
                    placeholder="Group"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                </div>

                {/* Page Name Input */}
                <div style={{ flex: "2 1 170px", minWidth: 0 }}>
                  <Input
                    value={pageTitle}
                    onChange={handleTitleChange}
                    placeholder="Page Name *"
                  />
                </div>

                {/* URL / Slug Input with Code-Driven Prefix & Suffix Addons */}
                <div style={{ flex: "2 1 220px", minWidth: 0 }}>
                  <Input
                    value={pageSlug}
                    onChange={handleSlugChange}
                    addonBefore={prefix || "/"}
                    addonAfter={suffix || null}
                    placeholder="page-slug"
                  />
                </div>

                {/* Status Select */}
                <div style={{ flex: "1 1 110px", maxWidth: 160 }}>
                  <Select
                    style={{ width: "100%" }}
                    value={pageStatus}
                    onChange={(val) => setPageStatus(val)}
                    options={[
                      { label: "Active", value: "ACTIVE" },
                      { label: "Draft", value: "DRAFT" },
                      { label: "Inactive", value: "INACTIVE" },
                    ]}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                </div>

                {/* Language Switcher Scope (Disabled for now, defaulted to English) */}
                <div style={{ flex: "1 1 120px", maxWidth: 170 }}>
                  <Tooltip title="Multi-language support coming soon (English default)">
                    <Select
                      disabled
                      style={{ width: "100%" }}
                      value={currentLang}
                      onChange={(val) => setCurrentLang(val)}
                      options={CMS_LANGUAGES.map((l) => ({
                        label: `${l.flag} ${l.label}`,
                        value: l.code,
                      }))}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                    />
                  </Tooltip>
                </div>
              </div>

              {/* Right: Save Canvas Button */}
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="middle"
                style={{
                  fontWeight: 600,
                  borderRadius: 6,
                  backgroundColor: "#8b0000",
                  borderColor: "#8b0000",
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                Save Canvas
              </Button>
            </div>
          </Card>

          {/* Main Builder Workspace: Left Fixed Full Height Library (210px) + Right Page Canvas (Flex 1) */}
          <div
            style={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              gap: "14px",
              alignItems: "stretch",
            }}
          >
            {/* Left Column: Slim Compact Component Library Sidebar (Fixed width 210px, Full height till bottom) */}
            <div
              style={{
                width: isDesktop ? "210px" : "100%",
                flexShrink: 0,
                position: isDesktop ? "sticky" : "static",
                top: 12,
                height: isDesktop ? "calc(100vh - 90px)" : 220,
                alignSelf: isDesktop ? "flex-start" : "stretch",
              }}
            >
              <ComponentLibrary
                onAdd={handleAddSection}
                insertAt={insertAt}
                onClearInsert={() => setInsertAt(null)}
                activeSections={sections}
                pageGroup={pageGroup}
              />
            </div>

            {/* Right Column: SEO Accordion & Block Canvas (Flex 1) */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* SEO Metadata Accordion */}
              <Collapse
                style={{
                  marginBottom: 12,
                  borderRadius: 8,
                  background: "#fff",
                }}
                items={[
                  {
                    key: "seo",
                    label: (
                      <Space>
                        <SettingOutlined style={{ color: "#1890ff" }} />
                        <strong>SEO Metadata & Search Engine Settings</strong>
                      </Space>
                    ),
                    children: (() => {
                      const exceptionGroups = ["header", "footer"];
                      const currentSlug = initialSlug || formFor?.slug || "";
                      const isException =
                        exceptionSlugs.includes(currentSlug) ||
                        exceptionGroups.includes(
                          String(pageGroup || "").toLowerCase(),
                        );
                      const siteUrl =
                        process.env.REACT_APP_CUSTOMER_SITE_URL || "";
                      const pathPrefix =
                        String(pageGroup).toLowerCase() === "blogs"
                          ? "blog/"
                          : "";
                      const fullUrl = `${siteUrl}/${pathPrefix}${currentSlug}`;

                      return (
                        <>
                          {!isException && currentSlug && (
                            <div style={{ marginBottom: 16, fontSize: 13 }}>
                              <span
                                style={{ color: "#4b5563", marginRight: 8 }}
                              >
                                Page URL:
                              </span>
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "#dc2626",
                                  textDecoration: "underline",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                {fullUrl}
                                <ExportOutlined />
                              </a>
                            </div>
                          )}
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item
                                label="Meta Title (SEO)"
                                style={{ marginBottom: 10 }}
                              >
                                <Input
                                  value={seoSetting?.meta_title || ""}
                                  onChange={(e) =>
                                    setSeoSetting({
                                      ...seoSetting,
                                      meta_title: e.target.value,
                                    })
                                  }
                                  placeholder={
                                    isServicePage
                                      ? "e.g. {{category_name}} | Infotainment Repair"
                                      : "Optimal length: 50-60 characters"
                                  }
                                />
                                {isServicePage && (
                                  <DynamicVariables
                                    compact
                                    label="Insert:"
                                    onPick={appendToSeo("meta_title")}
                                  />
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Meta Keywords"
                                style={{ marginBottom: 10 }}
                              >
                                <Input
                                  value={seoSetting?.meta_keywords || ""}
                                  onChange={(e) =>
                                    setSeoSetting({
                                      ...seoSetting,
                                      meta_keywords: e.target.value,
                                    })
                                  }
                                  placeholder="e.g. radio repair, infotainment navigation"
                                />
                                {isServicePage && (
                                  <DynamicVariables
                                    compact
                                    label="Insert:"
                                    onPick={appendToSeo("meta_keywords")}
                                  />
                                )}
                              </Form.Item>
                            </Col>
                          </Row>

                          <Form.Item
                            label="Meta Description (SEO)"
                            style={{ marginBottom: 0 }}
                          >
                            <TextArea
                              rows={2}
                              value={seoSetting?.meta_description || ""}
                              onChange={(e) =>
                                setSeoSetting({
                                  ...seoSetting,
                                  meta_description: e.target.value,
                                })
                              }
                              placeholder={
                                isServicePage
                                  ? "e.g. Expert {{category_name}} for car infotainment systems."
                                  : "Optimal length: 150-160 characters"
                              }
                            />
                            {isServicePage && (
                              <DynamicVariables
                                compact
                                label="Insert:"
                                onPick={appendToSeo("meta_description")}
                              />
                            )}
                          </Form.Item>
                        </>
                      );
                    })(),
                  },
                ]}
              />

              {isServicePage && (
                <Card
                  size="small"
                  style={{ marginBottom: 12, borderRadius: 8 }}
                  bodyStyle={{ padding: "10px 14px" }}
                >
                  <Text
                    style={{ display: "block", marginBottom: 6, fontSize: 13 }}
                  >
                    This is a service page. Use these variables in any section
                    text or SEO field; each category page shows its own value.
                    Click one to copy it.
                  </Text>
                  <DynamicVariables label="" />
                </Card>
              )}

              {/* Section Canvas List */}
              <div
                style={{
                  background: "#fcfcfd",
                  padding: "14px",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Space>
                    <BlockOutlined style={{ color: "#1890ff" }} />
                    <strong style={{ fontSize: 14 }}>
                      Page Canvas ({sections.length} Sections)
                    </strong>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Click card header to expand form editor
                  </Text>
                </div>

                {sections.length === 0 ? (
                  <Card
                    style={{
                      textAlign: "center",
                      padding: 30,
                      borderRadius: 8,
                    }}
                  >
                    <Empty description="No section blocks added yet. Click any component from the left sidebar to build your page." />
                  </Card>
                ) : (
                  <div>
                    <AddBetweenButton
                      active={insertAt === -1}
                      onClick={() => setInsertAt(insertAt === -1 ? null : -1)}
                    />

                    {sections.map((section, index) => (
                      <React.Fragment key={section.id || index}>
                        <BlockCard
                          form={form}
                          section={{
                            ...section,
                            data: { ...section.data, __pageStatus: pageStatus },
                          }}
                          index={index}
                          totalSections={sections.length}
                          isExpanded={expandedIndex === index}
                          onSelect={() => setExpandedIndex(index)}
                          onUpdateSection={handleUpdateSection}
                          onMoveUp={() => handleMoveSection(index, index - 1)}
                          onMoveDown={() => handleMoveSection(index, index + 1)}
                          onDuplicate={() => handleDuplicateSection(index)}
                          onDelete={() => handleDeleteSection(index)}
                        />

                        <AddBetweenButton
                          active={insertAt === index}
                          onClick={() =>
                            setInsertAt(insertAt === index ? null : index)
                          }
                        />
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Form>
    </ConfigProvider>,
    document.body,
  );
};

export default EditCMSPage;
